import { useState, type FormEvent } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Field';
import { cn } from '@/lib/utils';

export const TIMELINE_MAX_ITEMS = 10;

export interface TimelineItem {
  year: number | string;
  title: string;
  description: string;
}

interface Props {
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void | Promise<void>;
  submitting?: boolean;
  onCancel?: () => void;
}

function parseItems(value: Record<string, unknown>): TimelineItem[] {
  const raw = Array.isArray(value?.items) ? (value.items as TimelineItem[]) : [];
  return raw.map((it: TimelineItem) => ({
    year: typeof it.year === 'number' || typeof it.year === 'string' ? it.year : '',
    title: typeof it.title === 'string' ? it.title : '',
    description: typeof it.description === 'string' ? it.description : '',
  }));
}

export function TimelineEditForm({ value, onChange, submitting, onCancel }: Props) {
  const [items, setItems] = useState<TimelineItem[]>(parseItems(value));
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});

  function patchItem(idx: number, patch: Partial<TimelineItem>) {
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function addItem() {
    if (items.length >= TIMELINE_MAX_ITEMS) return;
    setItems((cur) => [...cur, { year: new Date().getFullYear(), title: '', description: '' }]);
  }

  function removeItem(idx: number) {
    setItems((cur) => cur.filter((_, i) => i !== idx));
    setErrors((cur) => {
      const next: Record<number, Record<string, string>> = {};
      for (const [k, v] of Object.entries(cur)) {
        const i = Number(k);
        if (i < idx) next[i] = v;
        else if (i > idx) next[i - 1] = v;
      }
      return next;
    });
  }

  function move(idx: number, direction: -1 | 1) {
    setItems((cur) => {
      const next = [...cur];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return cur;
      const a = next[idx];
      const b = next[target];
      if (!a || !b) return cur;
      next[idx] = b;
      next[target] = a;
      return next;
    });
  }

  function validate(): boolean {
    const next: Record<number, Record<string, string>> = {};
    items.forEach((it, idx) => {
      const itemErr: Record<string, string> = {};
      if (!String(it.year).toString().trim()) itemErr.year = 'Year is required';
      else if (typeof it.year === 'number' && (it.year < 1900 || it.year > 2200)) itemErr.year = 'Year out of range';
      if (!it.title.trim()) itemErr.title = 'Title is required';
      if (it.title.length > 120) itemErr.title = 'Title is too long';
      if (!it.description.trim()) itemErr.description = 'Description is required';
      if (it.description.length > 400) itemErr.description = 'Description is too long';
      if (Object.keys(itemErr).length > 0) next[idx] = itemErr;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!validate()) return;
    onChange({ items });
  }

  const atMax = items.length >= TIMELINE_MAX_ITEMS;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Timeline</p>
          <p className="mt-1 text-sm text-fg-muted">
            {items.length} of {TIMELINE_MAX_ITEMS} events
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={addItem}
          disabled={atMax}
        >
          Add event
        </Button>
      </div>

      {atMax && (
        <p className="text-xs text-fg-subtle" role="status">
          Maximum of {TIMELINE_MAX_ITEMS} events reached. Remove one to add another.
        </p>
      )}

      {items.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-bg-elevated/40 px-6 py-10 text-center text-sm text-fg-subtle">
          No timeline events yet. Click "Add event" to begin.
        </div>
      )}

      <ul className="space-y-4">
        {items.map((item, idx) => {
          const itemErr = errors[idx] ?? {};
          return (
            <li
              key={idx}
              className={cn(
                'rounded-md border bg-bg-elevated/40 p-4',
                Object.keys(itemErr).length > 0 ? 'border-danger/40' : 'border-border',
              )}
            >
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-medium text-fg-muted">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      aria-label="Move up"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="text-fg-subtle hover:text-fg disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      aria-label="Move down"
                      onClick={() => move(idx, 1)}
                      disabled={idx === items.length - 1}
                      className="text-fg-subtle hover:text-fg disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                  <GripVertical className="h-4 w-4 text-fg-subtle" />
                  <span>Event {idx + 1}</span>
                </div>
                <button
                  type="button"
                  aria-label={`Remove event ${idx + 1}`}
                  onClick={() => removeItem(idx)}
                  className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs text-fg-muted hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-[120px_1fr]">
                <Field id={`timeline-${idx}-year`} label="Year" required error={itemErr.year}>
                  <Input
                    id={`timeline-${idx}-year`}
                    type="number"
                    min={1900}
                    max={2200}
                    value={item.year}
                    onChange={(e) => {
                      const v = e.target.value;
                      patchItem(idx, { year: v === '' ? '' : Number(v) });
                    }}
                    placeholder="2026"
                  />
                </Field>
                <Field id={`timeline-${idx}-title`} label="Title" required error={itemErr.title}>
                  <Input
                    id={`timeline-${idx}-title`}
                    value={item.title}
                    onChange={(e) => patchItem(idx, { title: e.target.value })}
                    placeholder="What happened"
                  />
                </Field>
              </div>

              <div className="mt-3">
                <Field
                  id={`timeline-${idx}-description`}
                  label="Description"
                  required
                  error={itemErr.description}
                >
                  <Textarea
                    id={`timeline-${idx}-description`}
                    rows={2}
                    value={item.description}
                    onChange={(e) => patchItem(idx, { description: e.target.value })}
                    placeholder="A short description of this milestone"
                  />
                </Field>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" loading={submitting} disabled={items.length === 0}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
